import { useState, useEffect } from 'react';
import { Save, FileText } from 'lucide-react';
import axios from 'axios';
import AdminLayout from './AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pageContentStructure = {
  home: [
    { section: 'hero', label: 'Hero Section', fields: [
      { key: 'subtitle', label: 'Subtitle', type: 'text' },
      { key: 'title', label: 'Main Title', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
    ]},
    { section: 'testimonial', label: 'Testimonial', fields: [
      { key: 'quote', label: 'Quote', type: 'textarea' },
      { key: 'author', label: 'Author Name', type: 'text' },
    ]},
  ],
  about: [
    { section: 'hero', label: 'Hero Section', fields: [
      { key: 'subtitle', label: 'Subtitle', type: 'text' },
      { key: 'title', label: 'Main Title', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
    ]},
    { section: 'main', label: 'Main Content', fields: [
      { key: 'sectionTitle', label: 'Section Title', type: 'text' },
      { key: 'paragraph1', label: 'Paragraph 1', type: 'textarea' },
      { key: 'paragraph2', label: 'Paragraph 2', type: 'textarea' },
      { key: 'paragraph3', label: 'Paragraph 3', type: 'textarea' },
    ]},
  ],
};

const ContentAdmin = () => {
  const [selectedPage, setSelectedPage] = useState('home');
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert array to nested object
      const contentMap = {};
      response.data.forEach(item => {
        if (!contentMap[item.page]) contentMap[item.page] = {};
        contentMap[item.page][item.section] = item.content;
      });
      setContent(contentMap);
    } catch (err) {
      console.error('Failed to fetch content', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (page, section, key, value) => {
    setContent(prev => ({
      ...prev,
      [page]: {
        ...prev[page],
        [section]: {
          ...(prev[page]?.[section] || {}),
          [key]: value
        }
      }
    }));
  };

  const handleSave = async (page, section) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/admin/content/${page}/${section}`, {
        content: content[page]?.[section] || {}
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedMessage(`${page}/${section} saved!`);
      setTimeout(() => setSavedMessage(''), 3000);
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const pages = Object.keys(pageContentStructure);

  return (
    <AdminLayout>
      <div data-testid="content-admin-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-slate-900 uppercase">Page Content</h1>
          <p className="text-slate-500 mt-1">Edit text content across your website</p>
        </div>

        {savedMessage && (
          <div className="mb-4 p-3 bg-green-50 border-2 border-green-200 text-green-700">
            {savedMessage}
          </div>
        )}

        {/* Page Tabs */}
        <div className="flex gap-2 mb-6">
          {pages.map((page) => (
            <button
              key={page}
              onClick={() => setSelectedPage(page)}
              className={`px-4 py-2 font-heading text-sm uppercase ${
                selectedPage === page 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-white border-2 border-slate-100 text-slate-600 hover:border-orange-500'
              }`}
            >
              {page}
            </button>
          ))}
        </div>

        {/* Content Sections */}
        <div className="space-y-6">
          {pageContentStructure[selectedPage]?.map((sectionConfig) => (
            <div key={sectionConfig.section} className="bg-white border-2 border-slate-100">
              <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold text-slate-900 uppercase flex items-center gap-2">
                  <FileText className="w-5 h-5 text-orange-500" />
                  {sectionConfig.label}
                </h3>
                <button
                  onClick={() => handleSave(selectedPage, sectionConfig.section)}
                  disabled={saving}
                  className="btn-primary text-sm py-2 px-4 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {sectionConfig.fields.map((field) => (
                  <div key={field.key}>
                    <label className="block font-heading text-xs uppercase tracking-wider text-slate-500 mb-2">
                      {field.label}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea
                        value={content[selectedPage]?.[sectionConfig.section]?.[field.key] || ''}
                        onChange={(e) => handleFieldChange(selectedPage, sectionConfig.section, field.key, e.target.value)}
                        rows={4}
                        className="w-full px-4 py-3 border-2 border-slate-200 bg-slate-50 focus:border-orange-500"
                        placeholder={`Enter ${field.label.toLowerCase()}...`}
                      />
                    ) : (
                      <input
                        type="text"
                        value={content[selectedPage]?.[sectionConfig.section]?.[field.key] || ''}
                        onChange={(e) => handleFieldChange(selectedPage, sectionConfig.section, field.key, e.target.value)}
                        className="w-full h-12 px-4 border-2 border-slate-200 bg-slate-50 focus:border-orange-500"
                        placeholder={`Enter ${field.label.toLowerCase()}...`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 p-6 bg-slate-50 border-2 border-slate-100">
          <h3 className="font-heading text-lg font-bold text-slate-900 uppercase mb-2">Note</h3>
          <p className="text-slate-500 text-sm">
            For boat models, FAQ, and gallery images, use the dedicated management pages from the sidebar. 
            This page is for managing static text content on your pages.
          </p>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ContentAdmin;
